// skipcq: JS-W1028
import { useLayoutEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import { focusPortfolioBeerCard } from '../../utils/brewhousePortfolio/focusPortfolioBeerCard';
import PintPortfolioBeerCard from './PintPortfolioBeerCard';
import PintPortfolioZeroState from './PintPortfolioZeroState';

const RETURN_FOCUS_HIGHLIGHT_MS = 2000;

function schedulePortfolioBeerCardFocus(beerId, onComplete) {
  let cancelled = false;
  let highlightTimeoutId = 0;

  const frameId = window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }

      const focused = focusPortfolioBeerCard(beerId);
      if (!focused) {
        onComplete();
        return;
      }

      highlightTimeoutId = window.setTimeout(() => {
        if (!cancelled) {
          onComplete();
        }
      }, RETURN_FOCUS_HIGHLIGHT_MS);
    });
  });

  return () => {
    cancelled = true;
    window.cancelAnimationFrame(frameId);
    if (highlightTimeoutId > 0) {
      window.clearTimeout(highlightTimeoutId);
    }
  };
}

function PintPortfolioGrid({
  beers,
  loading,
  error = null,
  searchLoading = false,
  styleMetaMap,
  tagLabelMap,
  breweryNameById = {},
  onViewDetails,
  focusBeerId = '',
  onFocusBeerHandled = null,
}) {
  const [highlightedBeerId, setHighlightedBeerId] = useState('');

  useLayoutEffect(() => {
    if (
      loading ||
      searchLoading ||
      !Array.isArray(beers) ||
      typeof focusBeerId !== 'string' ||
      focusBeerId.length === 0
    ) {
      return undefined;
    }

    const beerIsVisible = beers.some((beer) => beer.id === focusBeerId);
    if (!beerIsVisible) {
      if (onFocusBeerHandled) {
        onFocusBeerHandled();
      }
      return undefined;
    }

    setHighlightedBeerId(focusBeerId);

    return schedulePortfolioBeerCardFocus(focusBeerId, () => {
      setHighlightedBeerId('');
      if (onFocusBeerHandled) {
        onFocusBeerHandled();
      }
    });
  }, [beers, focusBeerId, loading, onFocusBeerHandled, searchLoading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (searchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!Array.isArray(beers) || beers.length === 0) {
    return <PintPortfolioZeroState />;
  }

  return (
    <Grid container spacing={2}>
      {beers.map((beer) => (
        <Grid item xs={12} sm={6} lg={4} key={beer.id}>
          <PintPortfolioBeerCard
            beer={beer}
            styleMetaMap={styleMetaMap}
            tagLabelMap={tagLabelMap}
            breweryNameById={breweryNameById}
            onViewDetails={onViewDetails}
            highlighted={beer.id === highlightedBeerId}
          />
        </Grid>
      ))}
    </Grid>
  );
}

PintPortfolioGrid.propTypes = {
  beers: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  searchLoading: PropTypes.bool,
  styleMetaMap: PropTypes.object.isRequired,
  tagLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  breweryNameById: PropTypes.objectOf(PropTypes.string),
  onViewDetails: PropTypes.func.isRequired,
  focusBeerId: PropTypes.string,
  onFocusBeerHandled: PropTypes.func,
};

export default PintPortfolioGrid;
